
import { Box, styled } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { PageTab, PageTabs } from "components/shared/tabs";
import { useWorkItemContext, useWorkItemPermissions } from "hooks/work-items";
import { IWorkItemOverview } from "models/work-items";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { toPascalCase } from "utils/string-utils";


type TabInfo = {
    text: string;
    path: string;
    hasAlert?: (workItem: IWorkItemOverview) => boolean;
};

var tabs: TabInfo[] = [
    {
        text: 'OVERVIEW',
        path: '/overview'
    },
    {
        text: 'NOTES',
        path: '/notes'
    },
    {
        text: 'RESOURCE ALLOCATION',
        path: '/resources'
    },
    {
        text: 'RELATED WORK ITEMS',
        path: '/related'
    },
    {
        text: 'PRODUCT MASTER',
        path: '/product-master'
    },
    {
        text: 'METADATA',
        path: '/metadata',
        hasAlert: (workItem: IWorkItemOverview) => (workItem.requirements?.some(req => req.tabKey === 'metadata' && !req.isDone) ?? false)
    },
    {
        text: 'ATTACHMENTS',
        path: '/attachments',
        hasAlert: (workItem: IWorkItemOverview) => (workItem.requirements?.some(req => req.tabKey === 'attachments' && req.isDone === false) ?? false)
    },
    {
        text: 'WORKFLOWS',
        path: '/workflows'
    },
    {
        text: 'MY TASKS',
        path: '/tasks'

    }
];

type WorkItemTabsProps = {
    btnContainer: React.MutableRefObject<HTMLElement | null>;
};

const WorkItemTabs = (props: WorkItemTabsProps) => {
    const { btnContainer } = props;
    const { workItem } = useWorkItemContext();
    const permissions = useWorkItemPermissions();
    const location = useLocation();
    const [availableTabs, setAvailableTabs] = useState<TabInfo[]>(tabs);
    const [ selectedTabName, setSelectedTabName ] = useState('');

    useEffect(() => {
        for (let i = tabs.length - 1; i >= 0; i--) {
            if (location.pathname.includes(tabs[i].path)) {
                setSelectedTabName(tabs[i].text.toLowerCase().replace(' ', '-'));
                break;
            }
        }
    }, [location.pathname]);

    let selectedTab = 0;
    for (var i = availableTabs.length -1; i >= 0; i--) {
        if (location.pathname.includes(availableTabs[i].path)) {
            selectedTab = i;
            break;
        }
    }

    useEffect(() => {
        if (!permissions) return;

        const updatedTabs = [...tabs];

        const productMasterIndex = updatedTabs.findIndex(tab => tab.path === '/product-master');
        if (productMasterIndex >= 0 && !(
            permissions.canLinkProductMaster ||
            permissions.canUnlinkProductMaster
        )) {
            updatedTabs.splice(productMasterIndex, 1);
        }

        const workflowsIndex = updatedTabs.findIndex(tab => tab.path === '/workflows');
        if (workflowsIndex >= 0 && !permissions?.canViewWorkflows) {
            updatedTabs.splice(workflowsIndex, 1);
        }

        setAvailableTabs(updatedTabs);
    }, [permissions]);

    if (workItem === null) return null;

    return (
        <Box sx={{
            width: '100%',
            mb: '34px',
            paddingTop: '20px'
        }}>
            <Helmet>
                <title>Sage Central | Work Item | {toPascalCase(selectedTabName)}</title>
            </Helmet>
            <Grid container sx={{ alignItems: 'flex-end'}}>
                <PageTabs value={selectedTab} aria-label="work-items tabs">
                {
                    availableTabs.map((tab, index) => (
                        <PageTab
                            value={index}
                            selected={index === selectedTab}
                            text={tab.text}
                            href={`/workitems/${workItem.id}${tab.path}`}
                            hasAlert={tab.hasAlert?.(workItem) ?? false}
                        />
                    ))
                }
                </PageTabs>
                <Grid xs={4} mdOffset='auto' >
                    <Box sx={{
                        float: 'right',
                        display: 'flex',
                        margin: '8px',
                        marginRight: 0
                    }}>
                        <Box ref={btnContainer} />
                    </Box>
                </Grid>
            </Grid>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: '-2px', }}></Box>
        </Box>
    );
};

export default WorkItemTabs;